import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLanguage, productsCopy } from "../i18n";
import { Logo } from "./Logo";
import { MagneticWrap } from "./MagneticWrap";
import { scrollToSection } from "../lib/scroll";

const LINKS = [
  { href: "#kurumsal", key: "about", testid: "nav-link-about" },
  { href: "#urunler", key: "products", testid: "nav-link-products" },
  { href: "#faaliyetler", key: "services", testid: "nav-link-services" },
  { href: "#surdurulebilirlik", key: "sustainability", testid: "nav-link-sustainability" },
  { href: "#iletisim", key: "contact", testid: "nav-link-contact" },
];

export const Navbar = ({ onQuote }) => {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const labelFor = (l) =>
    l.key === "products" ? (productsCopy[lang] || productsCopy.tr).navLabel : t.nav[l.key];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(href);
  };

  const LangToggle = ({ className = "" }) => (
    <div
      className={`flex items-center rounded-full border border-white/15 bg-white/5 p-1 ${className}`}
      data-testid="lang-toggle-button"
    >
      {["tr", "en", "ru"].map((l) => (
        <button
          key={l}
          data-testid={`lang-option-${l}`}
          onClick={() => setLang(l)}
          className={`rounded-full px-3 py-1 font-mono text-xs font-semibold uppercase tracking-widest transition-colors duration-300 ${
            lang === l ? "bg-[#48e38e] text-[#062316]" : "text-white/60 hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#07170f]/55 shadow-[0_12px_44px_rgba(0,0,0,0.4)] backdrop-blur-xl"
            : "bg-[#07170f]/40 backdrop-blur-md"
        }`}
      >
        <span className="pointer-events-none absolute bottom-0 left-0 h-px w-full bg-gradient-to-r from-transparent via-[#48e38e]/50 to-transparent" />
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
          <a href="#" onClick={(e) => go(e, "#hero")} aria-label="Markagro Global">
            <Logo />
          </a>

          <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-menu">
            {LINKS.map((l) => (
              <a
                key={l.key}
                href={l.href}
                data-testid={l.testid}
                onClick={(e) => go(e, l.href)}
                className="group relative text-sm font-medium text-white/75 transition-colors duration-300 hover:text-white"
              >
                {labelFor(l)}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[#48e38e] transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LangToggle className="hidden sm:flex" />
            <MagneticWrap strength={0.25}>
              <button
                data-testid="quote-nav-button"
                onClick={onQuote}
                className="btn-sweep group hidden items-center gap-1.5 rounded-full bg-[#48e38e] px-5 py-2.5 text-sm font-semibold text-[#062316] transition-shadow duration-300 hover:shadow-[0_0_24px_rgba(72,227,142,0.45)] sm:flex"
              >
                {t.nav.quote}
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </MagneticWrap>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white lg:hidden"
              aria-label="Menu"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-[#04120b]"
          >
            <div className="flex h-[72px] items-center justify-between px-5 sm:px-8">
              <Logo />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col items-center justify-center gap-2 px-8 text-center">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.key}
                  href={l.href}
                  data-testid={`mobile-${l.testid}`}
                  onClick={(e) => go(e, l.href)}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="font-display text-4xl font-bold text-white transition-colors hover:text-[#48e38e]"
                >
                  {labelFor(l)}
                </motion.a>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.5 }}
                className="mt-8 flex items-center gap-4"
              >
                <LangToggle />
                <button
                  data-testid="quote-mobile-button"
                  onClick={() => {
                    setOpen(false);
                    onQuote();
                  }}
                  className="rounded-full bg-[#48e38e] px-6 py-3 text-sm font-semibold text-[#062316]"
                >
                  {t.nav.quote}
                </button>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
